import { Dialog, Transition } from '@headlessui/react';
import cx from 'classnames';
import {
  useRef,
  useEffect,
  FunctionComponent,
  ComponentProps,
  ReactNode,
  Fragment,
  useCallback,
  useState,
  forwardRef,
} from 'react';

import useOnClickOutside from '../../../hooks/click-outside';
import Button from '../../ui/button';
import IconClose from '../../ui/icons/close';

const CloseButton: FunctionComponent<
  ComponentProps<'button'> & { closeBorderLeft?: boolean }
> = ({ closeBorderLeft = false, ...props }) => (
  <div
    className={cx(
      'absolute right-0 top-0',
      { 'border-l border-neutral-30 lg:border-none': closeBorderLeft },
      'z-200'
    )}
    data-cy="close-slider"
  >
    <Button {...props}>
      <IconClose className={cx('text-neutral-80', 'w-4')} />
    </Button>
  </div>
);

const leftProps = {
  enter: 'duration-500 ease-out',
  enterFrom: '-translate-x-full before:opacity-0',
  enterTo: 'translate-x-0 before:opacity-100',
  leave: 'duration-500 ease-out',
  leaveFrom: 'translate-x-0 before:opacity-100',
  leaveTo: '-translate-x-full before:opacity-0',
};

const rightProps = {
  enter: 'duration-500 ease-out',
  enterFrom: 'translate-x-full before:opacity-0',
  enterTo: 'translate-x-0 before:opacity-100',
  leave: 'duration-500 ease-out',
  leaveFrom: 'translate-x-0 before:opacity-100',
  leaveTo: 'translate-x-full before:opacity-0',
};

const Slider: FunctionComponent<{
  isOpen: boolean;
  children: ReactNode;
  enterFrom?: 'left' | 'right';
  isFullscreenMobile?: boolean;
  onClose: () => void;
  closeBorderLeft?: boolean;
  closeButtonAvailable?: boolean;
  classes?: any;
}> = ({
  children,
  enterFrom = 'left',
  isFullscreenMobile,
  isOpen,
  onClose,
  closeBorderLeft = false,
  closeButtonAvailable = true,
  classes,
}) => {
  const animation = enterFrom === 'left' ? leftProps : rightProps;
  const ref = useRef(null);

  //useOnClickOutside(ref, onClose);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'auto';
    }

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
  }, [isOpen]);

  return (
    <Transition
      ref={ref}
      unmount={false}
      show={isOpen}
      as={Fragment}
      {...animation}
    >
      <Dialog
        unmount={false}
        onClose={onClose}
        className={cx(
          'h-full before:will-change-auto top-0 fixed z-200 flex will-change-transform transition-all ease-out before:duration-500 duration-500',
          'before:block before:absolute before:min-w-[100vw] before:min-h-screen before:bg-[#2b2b2b68] before:-z-10',
          isFullscreenMobile
            ? 'w-full md:max-w-sm'
            : 'w-[calc(100vw-3rem)] md:max-w-sm',
          enterFrom === 'left'
            ? 'left-0 before:left-full'
            : 'right-0 before:right-full'
        )}
      >
        <Dialog.Panel
          className={
            'relative bg-white z-10 max-h-[100vh] overflow-auto w-full'
          }
        >
          {closeButtonAvailable && (
            <CloseButton
              closeBorderLeft={closeBorderLeft}
              tabIndex={isOpen ? 0 : -1}
              onClick={onClose}
            />
          )}
          <div
            ref={ref}
            className={cx(
              'relative bg-white z-10 max-h-[100vh] shadow-md overflow-auto',
              'h-full left-0',
              isFullscreenMobile
                ? 'w-full md:max-w-sm'
                : 'w-[calc(100vw-3rem)] md:max-w-sm'
            )}
            style={classes}
          >
            {children}
          </div>
        </Dialog.Panel>
      </Dialog>
      {/* <div className="bg-red-100 h-screen w-[300px] absolute top-0 right-0 z-[1000]">
        <div className="bg-blue-100 h-[500px] w-[300px] absolute top-0 right-0 z-[1000]">
          {' '}
          miau{' '}
        </div>
       
      </div> */}
    </Transition>
  );
};

export default Slider;
